import React, { FC } from 'react';
import { ForgeButton, ForgeIcon, ForgeIconButton, ForgeSelect } from '@tylertech/forge-react';

import './index.scss';
import { isEmpty } from 'lodash';
import { ExportFormat, scheduledSendGeospatialFormats } from 'common/downloadLinks';
import { AttachmentConfiguration, TableAttachment } from '../../types/attachment';
import { DEFAULT_ATTACHMENT_CONFIG, REPORT_ATTACHMENT_ICON, TABLE_ATTACHMENT_ICON } from '../../constants';
import { I18n } from 'common/visualizations';
import { getExportFormatOptions, getTableOptions } from './helpers';
import { ScheduleSendValues } from '../../types/scheduleSend';
import PdfConfiguration from './PdfConfiguration';

const scope = 'shared.components.schedule_send_report.modal.attachment_section';

export interface AttachmentSectionProps {
  tableList: TableAttachment[] | void;
  attachmentConfigs: AttachmentConfiguration[];
  updateScheduleSendValue: <K extends keyof ScheduleSendValues>(key: K, value: ScheduleSendValues[K]) => void;
}

const AttachmentSection: FC<AttachmentSectionProps> = (props) => {
  const { tableList, attachmentConfigs, updateScheduleSendValue } = props;

  const handleAddAttachment = () => {
    updateScheduleSendValue('attachmentConfigs', [
      ...attachmentConfigs,
      {
        value: DEFAULT_ATTACHMENT_CONFIG.value,
        format: DEFAULT_ATTACHMENT_CONFIG.format,
        icon: DEFAULT_ATTACHMENT_CONFIG.icon
      }
    ]);
  };

  const handleRemoveAttachment = (index: number) => {
    const newAttachmentConfigs = attachmentConfigs.filter((_, i) => i !== index);
    updateScheduleSendValue('attachmentConfigs', newAttachmentConfigs);
  };

  const onTableChange = (index: number, event: CustomEvent) => {
    const value = event.detail as string;
    const newAttachmentConfigs = [...attachmentConfigs];
    newAttachmentConfigs[index].value = value;

    // we only support pdf format type for full report attachment
    const tableConfig = tableList?.find((table) => table.identifier === value);
    const isTableWithGeoData = tableConfig?.withGeoSpatialData;

    // if selected table does not contain geospatial column and the selected export type is for GeoSpatial formats only.
    const isGeoDataNotApplicable =
      !isTableWithGeoData &&
      scheduledSendGeospatialFormats.includes(attachmentConfigs[index].format as ExportFormat);

    if (value === 'full-report' || isGeoDataNotApplicable) {
      newAttachmentConfigs[index].format = 'pdf';
      newAttachmentConfigs[index].icon = REPORT_ATTACHMENT_ICON;
    } else {
      newAttachmentConfigs[index].icon = TABLE_ATTACHMENT_ICON;
      newAttachmentConfigs[index].tableAttachment = tableConfig;
    }

    updateScheduleSendValue('attachmentConfigs', newAttachmentConfigs);
  };

  const onFormatChange = (index: number, event: CustomEvent) => {
    const value = event.detail as string;
    const newAttachmentConfigs = [...attachmentConfigs];
    newAttachmentConfigs[index].format = value;
    updateScheduleSendValue('attachmentConfigs', newAttachmentConfigs);
  };

  const attachmentOptions = (index: number, totalAttachments: number) => {
    const tableCurrentValue = isEmpty(attachmentConfigs[index].value)
      ? DEFAULT_ATTACHMENT_CONFIG.value
      : attachmentConfigs[index].value;

    const formatCurrentValue = isEmpty(attachmentConfigs[index].format)
      ? DEFAULT_ATTACHMENT_CONFIG.format
      : attachmentConfigs[index].format;

    const isTableWithGrouping = tableList?.find((table) => table.identifier === tableCurrentValue)
      ?.withGrouping as boolean;

    return (
      <div className="attachment-select-dropdrowns">
        <ForgeSelect
          className="attachment-table-list"
          data-testid="attachment-table-select"
          value={tableCurrentValue}
          options={getTableOptions(tableList)}
          syncPopupWidth
          on-change={(event: CustomEvent) => onTableChange(index, event)}
        >
          <ForgeIcon slot="start" name={attachmentConfigs[index].icon} />
        </ForgeSelect>

        <ForgeSelect
          className="attachment-format-list"
          data-testid="attachment-format-select"
          value={formatCurrentValue}
          options={getExportFormatOptions(tableList, tableCurrentValue)}
          disabled={tableCurrentValue === 'full-report'}
          syncPopupWidth
          on-change={(event: CustomEvent) => onFormatChange(index, event)}
        />

        <PdfConfiguration
          index={index}
          tableCurrentValue={tableCurrentValue}
          formatCurrentValue={formatCurrentValue}
          attachmentConfigs={attachmentConfigs}
          updateScheduleSendValue={updateScheduleSendValue}
          isTableWithGrouping={isTableWithGrouping}
        />

        <ForgeIconButton
          densityLevel={3}
          slot="trailing"
          data-testid="remove-attachment-btn"
          className="remove-attachment"
          hidden={totalAttachments <= 1}
          dense
          onClick={() => {
            handleRemoveAttachment(index);
          }}
        >
          <ForgeIcon slot="end" name="close" />
        </ForgeIconButton>
      </div>
    );
  };

  return (
    <div className="attachment-section" data-testid="attachment-section">
      <div className="contents">
        <h1 className="forge-typography--heading3" id="attachment-title" data-testid="attachment-title">
          {I18n.t('title', { scope })}
        </h1>
        <div className="select-section">
          {attachmentConfigs.map((config, index) => (
            <div key={`${config.value}-${index}`}>{attachmentOptions(index, attachmentConfigs.length)}</div>
          ))}
        </div>

        <ForgeButton
          data-testid="add-attachment-btn"
          className="add-attachment"
          onClick={handleAddAttachment}
        >
          <ForgeIcon name="add" />
          <span>{I18n.t('add', { scope })}</span>
        </ForgeButton>
      </div>
    </div>
  );
};

export default AttachmentSection;
